/**
 * Generating a triangle mesh from design material SDF patterns on top of a complex base mesh.
 * @module
 */
import { aabb2AddPoint, aabb2ByArray, aabb2ByTwoPoints, aabb2XLength, aabb2YLength } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, ZERO_EPSILON } from "geom/core";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import type { Mesh3 } from "geom/mesh";
import { planeByThreePoints, planeNewXY, planeNormal, planeProject3 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { pushXYZ } from "geom/utils";
import type { Vec2 } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Copy, vec3Cross, vec3Dist, vec3Dot, vec3Normalize, vec3Sub } from "geom/vec3";
import { WeldingDuplicateHandling, weldVertices } from "geom/welding";
import type {
  SdfCommonParamsPreprocessed,
  SdfParamsPreprocessed,
  SdfPatternPreprocessed,
} from "../designMaterialTypes";
import type { UVFace } from "./discretizationTypes";
import { clearCellGeometryCache, fetchCellGeometry } from "./generateCellGeometry";
import type { GeoBuilder } from "./geoBuilder";
import { compactMesh3FromGeoBuilder, geoBuilderAppend, geoBuilderNew } from "./geoBuilder";
import { sdfCellBoundariesInAABB } from "./sdfLayout";
import type { AdjustGeoType, MaterialLayoutInfo } from "./utils";
import { rotateVertsZ, scaleVerts, translateVerts } from "./utils";

interface PlaneWithNormal {
  plane: Plane;
  nx: number;
  ny: number;
  nz: number;
}

interface FaceAndPlanes {
  face: UVFace;
  uvClipPlanes: PlaneWithNormal[];
  startIndex: number; // in the index buffer
  flipWindings: boolean;
}

function uvFaceNew(numVerts: number): UVFace {
  return {
    u: new Float32Array(numVerts),
    v: new Float32Array(numVerts),
    x: new Float32Array(numVerts),
    y: new Float32Array(numVerts),
    z: new Float32Array(numVerts),
    nx: new Float32Array(numVerts),
    ny: new Float32Array(numVerts),
    nz: new Float32Array(numVerts),
  };
}

const reduceMin = (a: number, b: number) => Math.min(a, b);
const reduceMax = (a: number, b: number) => Math.max(a, b);

function computeUvAABB(groupFaces: FaceAndPlanes[]): AABB2 {
  let minU = 99999999;
  let maxU = -minU;
  let minV = minU;
  let maxV = -minV;
  for (const fp of groupFaces) {
    const faceUV = fp.face;
    minU = Math.min(minU, faceUV.u.reduce(reduceMin));
    maxU = Math.max(maxU, faceUV.u.reduce(reduceMax));
    minV = Math.min(minV, faceUV.v.reduce(reduceMin));
    maxV = Math.max(maxV, faceUV.v.reduce(reduceMax));
  }
  return aabb2New(minU, minV, maxU, maxV);
}

function intersectRaySegmentWithPlane(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  p: PlaneWithNormal,
) {
  const { nx, ny, nz } = p;
  const { originX, originY, originZ } = p.plane;
  const [rx, ry, rz] = vec3Normalize(x0 - x1, y0 - y1, z0 - z1);
  const ret = intersectRay3PlaneWithNormal(x0, y0, z0, rx, ry, rz, originX, originY, originZ, nx, ny, nz);
  if (ret) {
    return ret;
  } else {
    return [x1, y1, z1];
  }
}

export function signedArea2(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): number {
  const ret = (x1 - x0) * (y2 - y1) - (x2 - x1) * (y1 - y0);
  return ret / 2.0;
}

function signedArea3(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
): number {
  const [ax, ay, az] = vec3Sub(x1, y1, z1, x0, y0, z0);
  const [bx, by, bz] = vec3Sub(x2, y2, z2, x0, y0, z0);
  const [cx, cy, cz] = vec3Cross(ax, ay, az, bx, by, bz);
  const areax2 = vec3Dot(cx, cy, cz, cx, cy, cz);
  return areax2 / 2.0;
}

function planeSignedDistanceTo(plane: PlaneWithNormal, x: number, y: number, z: number) {
  const { originX, originY, originZ } = plane.plane;
  const { nx, ny, nz } = plane;
  return vec3Dot(x - originX, y - originY, z - originZ, nx, ny, nz);
}

function planeWithNormalIsCoplanar(plane0: Plane, plane0Normal: Vec3, plane1: Plane, plane1Normal: Vec3): boolean {
  const DIST_EPSILON = 1e-4;

  const [nx0, ny0, nz0] = plane0Normal;
  const [nx1, ny1, nz1] = plane1Normal;

  const dot = vec3Dot(nx0, ny0, nz0, nx1, ny1, nz1);
  if (Math.abs(Math.abs(dot) - 1) > DIST_EPSILON) {
    return false;
  }

  const [px, py, pz] = planeProject3(plane0, plane1.originX, plane1.originY, plane1.originZ);
  const dist = vec3Dist(px, py, pz, plane1.originX, plane1.originY, plane1.originZ);

  return dist < DIST_EPSILON;
}

function addClippedFaceToGeo(
  geo: GeoBuilder,
  firstIndex: number,
  secondIndex: number,
  thirdIndex: number,
  clipPlanes: PlaneWithNormal[],
  flipWindings: boolean,
) {
  const addNewVert = (x: number, y: number, z: number) => {
    const newIndex = geo.vertices.length / 3;
    pushXYZ(geo.vertices, x, y, z);
    const ret = geo.indices.length;
    geo.indices.push(newIndex);
    return ret;
  };
  const firstVertexIndex = geo.indices[firstIndex] * 3;
  const x0 = geo.vertices[firstVertexIndex];
  const y0 = geo.vertices[firstVertexIndex + 1];
  const z0 = geo.vertices[firstVertexIndex + 2];
  const secondVertexIndex = geo.indices[secondIndex] * 3;
  const x1 = geo.vertices[secondVertexIndex];
  const y1 = geo.vertices[secondVertexIndex + 1];
  const z1 = geo.vertices[secondVertexIndex + 2];
  const thirdVertexIndex = geo.indices[thirdIndex] * 3;
  const x2 = geo.vertices[thirdVertexIndex];
  const y2 = geo.vertices[thirdVertexIndex + 1];
  const z2 = geo.vertices[thirdVertexIndex + 2];

  if (signedArea3(x0, y0, z0, x1, y1, z1, x2, y2, z2) < ZERO_EPSILON) {
    return;
  }

  for (let i = 0, l = clipPlanes.length; i < l; i++) {
    const plane = clipPlanes[i];
    const p0Dist = planeSignedDistanceTo(plane, x0, y0, z0);
    const p1Dist = planeSignedDistanceTo(plane, x1, y1, z1);
    const p2Dist = planeSignedDistanceTo(plane, x2, y2, z2);
    let numBehind = p0Dist <= 0 ? 1 : 0;
    numBehind += p1Dist <= 0 ? 1 : 0;
    numBehind += p2Dist <= 0 ? 1 : 0;
    if (numBehind === 3) {
      // All points behind the plane, don't add triangle.
      return;
    }
    if (numBehind === 0) {
      continue;
    }
    const vx = [x0, x1, x2];
    const vy = [y0, y1, y2];
    const vz = [z0, z1, z2];

    if (numBehind === 2) {
      // Two points behind the plane, one up front.  Shorten the two lengths to
      // match plane.
      let frontIndex = 0; // init to p0 in front
      let indexA = 1;
      let indexB = 2;
      if (p1Dist > 0) {
        frontIndex = 1;
        indexA = 0;
        indexB = 2;
      } else if (p2Dist > 0) {
        frontIndex = 2;
        indexA = 0;
        indexB = 1;
      }
      const [nxa, nya, nza] = intersectRaySegmentWithPlane(
        vx[frontIndex],
        vy[frontIndex],
        vz[frontIndex],
        vx[indexA],
        vy[indexA],
        vz[indexA],
        plane,
      );
      const newVertIndices0 = [firstIndex, secondIndex, thirdIndex];
      newVertIndices0[indexA] = addNewVert(nxa, nya, nza);
      const [nxb, nyb, nzb] = intersectRaySegmentWithPlane(
        vx[frontIndex],
        vy[frontIndex],
        vz[frontIndex],
        vx[indexB],
        vy[indexB],
        vz[indexB],
        plane,
      );
      newVertIndices0[indexB] = addNewVert(nxb, nyb, nzb);

      addClippedFaceToGeo(
        geo,
        newVertIndices0[0],
        newVertIndices0[1],
        newVertIndices0[2],
        clipPlanes.slice(1),
        flipWindings,
      );
      return;
    } else if (numBehind === 1) {
      // One point behind the plane, create two new triangles
      let frontIndexA = 0;
      let frontIndexB = 1;
      let backIndex = 2;
      if (p0Dist <= 0) {
        backIndex = 0;
        frontIndexA = 1;
        frontIndexB = 2;
      } else if (p1Dist <= 0) {
        backIndex = 1;
        frontIndexA = 0;
        frontIndexB = 2;
      }
      const newPlanes = clipPlanes.slice(i + 1);

      // Create first triangle
      const [ax, ay, az] = intersectRaySegmentWithPlane(
        vx[frontIndexA],
        vy[frontIndexA],
        vz[frontIndexA],
        vx[backIndex],
        vy[backIndex],
        vz[backIndex],
        plane,
      );
      const newVertIndices0 = [firstIndex, secondIndex, thirdIndex];
      newVertIndices0[backIndex] = addNewVert(ax, ay, az);
      addClippedFaceToGeo(geo, newVertIndices0[0], newVertIndices0[1], newVertIndices0[2], newPlanes, flipWindings);

      // Create second triangle
      const [bx, by, bz] = intersectRaySegmentWithPlane(
        vx[frontIndexB],
        vy[frontIndexB],
        vz[frontIndexB],
        vx[backIndex],
        vy[backIndex],
        vz[backIndex],
        plane,
      );
      const newVertIndices1 = [firstIndex, secondIndex, thirdIndex];
      newVertIndices1[backIndex] = addNewVert(bx, by, bz);
      newVertIndices1[frontIndexA] = newVertIndices0[backIndex];

      addClippedFaceToGeo(geo, newVertIndices1[0], newVertIndices1[1], newVertIndices1[2], newPlanes, flipWindings);
      return;
    }
  }

  if (!flipWindings) {
    pushXYZ(geo.newIndices, geo.indices[firstIndex], geo.indices[secondIndex], geo.indices[thirdIndex]);
  } else {
    pushXYZ(geo.newIndices, geo.indices[firstIndex], geo.indices[thirdIndex], geo.indices[secondIndex]);
  }
}

function addFaceToGeo(
  geo: GeoBuilder,
  firstIndex: number,
  baseGeometryFaces: FaceAndPlanes[],
  clipPlanes: PlaneWithNormal[],
  flipWindings: boolean,
) {
  const secondIndex = firstIndex + 1;
  const thirdIndex = firstIndex + 2;

  const firstVertexIndex = geo.indices[firstIndex] * 3;
  const x0 = geo.vertices[firstVertexIndex];
  const y0 = geo.vertices[firstVertexIndex + 1];
  const z0 = geo.vertices[firstVertexIndex + 2];
  const secondVertexIndex = geo.indices[secondIndex] * 3;
  const x1 = geo.vertices[secondVertexIndex];
  const y1 = geo.vertices[secondVertexIndex + 1];
  const z1 = geo.vertices[secondVertexIndex + 2];
  const thirdVertexIndex = geo.indices[thirdIndex] * 3;
  const x2 = geo.vertices[thirdVertexIndex];
  const y2 = geo.vertices[thirdVertexIndex + 1];
  const z2 = geo.vertices[thirdVertexIndex + 2];

  if (signedArea3(x0, y0, z0, x1, y1, z1, x2, y2, z2) === 0.0) {
    return;
  }

  let p0FaceIndex = -1;
  let p1FaceIndex = -1;
  let p2FaceIndex = -1;
  // Run through all of baseGeometryFaces check for if all points are inside of any face
  for (let i = 0, l = baseGeometryFaces.length; i < l; i++) {
    const face = baseGeometryFaces[i];
    let p0Front = 0;
    let p1Front = 0;
    let p2Front = 0;
    for (const plane of face.uvClipPlanes) {
      if (p0FaceIndex === -1) {
        p0Front = planeSignedDistanceTo(plane, x0, y0, z0) >= 0 ? p0Front + 1 : p0Front;
      }
      if (p1FaceIndex === -1) {
        p1Front = planeSignedDistanceTo(plane, x1, y1, z1) >= 0 ? p1Front + 1 : p1Front;
      }
      if (p2FaceIndex === -1) {
        p2Front = planeSignedDistanceTo(plane, x2, y2, z2) >= 0 ? p2Front + 1 : p2Front;
      }
    }
    const numPlanes = face.uvClipPlanes.length;
    if (p0Front === numPlanes) {
      p0FaceIndex = i;
    }
    if (p1Front === numPlanes) {
      p1FaceIndex = i;
    }
    if (p2Front === numPlanes) {
      p2FaceIndex = i;
    }
  }
  // If all points are inside any face, just clip against global clip planes
  if (p0FaceIndex !== -1 && p1FaceIndex !== -1 && p2FaceIndex !== -1) {
    addClippedFaceToGeo(geo, firstIndex, secondIndex, thirdIndex, clipPlanes, flipWindings);
    geo.indicesToTransform.push({ endIndex: geo.newIndices.length, face: baseGeometryFaces[p0FaceIndex].face });
  } else {
    // If not, run through baseGeometryFaces, clip against everything.
    for (const face of baseGeometryFaces) {
      addClippedFaceToGeo(geo, firstIndex, secondIndex, thirdIndex, face.uvClipPlanes.concat(clipPlanes), flipWindings);
      geo.indicesToTransform.push({ endIndex: geo.newIndices.length, face: face.face });
    }
  }
}

function convertUVToObjectSpace(geo: GeoBuilder, scale: Vec3) {
  let currIndex = 0;
  const transformedVertIndices = {};
  const oldVertices = Array.from(geo.vertices);
  for (const { endIndex, face } of geo.indicesToTransform) {
    for (; currIndex < endIndex; currIndex++) {
      const vertIndex = geo.newIndices[currIndex];
      const vertOffset = vertIndex * 3;

      const notTransformed = !transformedVertIndices[vertIndex];

      const u = notTransformed ? geo.vertices[vertOffset + 0] : oldVertices[vertOffset + 0];
      const v = notTransformed ? geo.vertices[vertOffset + 1] : oldVertices[vertOffset + 1];
      const w = notTransformed ? geo.vertices[vertOffset + 2] : oldVertices[vertOffset + 2];

      const v0Area = signedArea2(u, v, face.u[1], face.v[1], face.u[2], face.v[2]);
      const v1Area = signedArea2(u, v, face.u[2], face.v[2], face.u[0], face.v[0]);
      const v2Area = signedArea2(u, v, face.u[0], face.v[0], face.u[1], face.v[1]);
      const totalArea = v0Area + v1Area + v2Area;

      const v0w = v0Area / totalArea;
      const v1w = v1Area / totalArea;
      const v2w = v2Area / totalArea;

      const x: number[] = new Array(3);
      const y: number[] = new Array(3);
      const z: number[] = new Array(3);
      for (let i = 0; i < 3; i++) {
        x[i] = face.x[i] + face.nx[i] * w * (1.0 / scale[0]);
        y[i] = face.y[i] + face.ny[i] * w * (1.0 / scale[1]);
        z[i] = face.z[i] + face.nz[i] * w * (1.0 / scale[2]);
      }

      const newX = v0w * x[0] + v1w * x[1] + v2w * x[2];
      const newY = v0w * y[0] + v1w * y[1] + v2w * y[2];
      const newZ = v0w * z[0] + v1w * z[1] + v2w * z[2];

      if (notTransformed) {
        geo.vertices[vertOffset + 0] = newX;
        geo.vertices[vertOffset + 1] = newY;
        geo.vertices[vertOffset + 2] = newZ;

        transformedVertIndices[vertIndex] = face;
      } else {
        // This vert isn't actually shared, create a new one.
        if (
          geo.vertices[vertOffset + 0] !== newX ||
          geo.vertices[vertOffset + 1] !== newY ||
          geo.vertices[vertOffset + 2] !== newZ
        ) {
          const newIndex = geo.vertices.length / 3;
          pushXYZ(geo.vertices, newX, newY, newZ);
          geo.newIndices[currIndex] = newIndex;
        }
      }
    }
  }
}

function convertUVBufferToWorldGeometry(
  vertices: Float32Array,
  indices: Uint8Array | Uint32Array,
  baseGeometryFaces: FaceAndPlanes[],
  uvOffset: Vec2,
  clipPlanes: PlaneWithNormal[],
  flipWindings: boolean,
  scale: Vec3,
) {
  const geo: GeoBuilder = geoBuilderNew(vertices, indices);
  for (let i = 0, ic = indices.length; i < ic; i += 3) {
    addFaceToGeo(geo, i, baseGeometryFaces, clipPlanes, flipWindings);
  }
  convertUVToObjectSpace(geo, scale);
  geo.uvs = new Array((geo.vertices.length / 3) * 2);
  for (let i = 0, j = 0, ic = geo.vertices.length; i < ic; i += 3, j += 2) {
    geo.uvs[j] = geo.vertices[i] + uvOffset[0];
    geo.uvs[j + 1] = geo.vertices[i + 1] + uvOffset[1];
  }
  return geo;
}

/** Both params are mutated. */
function getLayoutOffset(aabb: AABB2, layoutInfo: MaterialLayoutInfo): Vec2 {
  const height = layoutInfo.length > 0 ? layoutInfo[layoutInfo.length - 1].maxY : 0;
  const targetWidth = height;
  const aabbLength = aabb2XLength(aabb);
  const aabbHeight = aabb2YLength(aabb);
  for (let i = 0, ic = layoutInfo.length; i < ic; i++) {
    const row = layoutInfo[i];
    const rowLength = aabb2XLength(row);
    const rowHeight = aabb2YLength(row);
    const isLastRow = layoutInfo.length === -1;
    if (rowLength + aabbLength <= targetWidth && (aabbHeight <= rowHeight || isLastRow)) {
      // add to this row by offseting in x/y, update height if needed
      const uOffset = rowLength - aabb.minX;
      const vOffset = row.minY - aabb.minY;
      if (isLastRow) {
        if (rowHeight < aabbHeight) {
          row.maxY += aabbHeight - rowHeight;
        }
      }
      row.maxX += aabbLength;
      return [uOffset, vOffset];
    }
  }
  // Didn't find a row, create a new one.
  const uOffset = -aabb.minX;
  const vOffset = height - aabb.minY;
  layoutInfo.push(aabb2ByTwoPoints(0, height, aabbLength, height + aabbHeight));
  return [uOffset, vOffset];
}

function gatherGroupedFacesAndPlanes(baseGeo: Mesh3): { groupedFaces: FaceAndPlanes[][]; groupNormals: Vec3[] } {
  const ungroupedBaseGeometryFaces: FaceAndPlanes[] = [];
  const groupedFaces: FaceAndPlanes[][] = [];
  const groupPlanes: Plane[] = [];
  const groupNormals: Vec3[] = [];
  const { uvs1: uv, verts: position, norms: normal } = baseGeo;
  if (!uv || !normal) {
    return { groupedFaces, groupNormals };
  }
  for (let i = 0, l = baseGeo.tris.length; i < l; i += 3) {
    const faceUV = uvFaceNew(3);
    for (let j = 0; j < 3; j++) {
      const index = baseGeo.tris[i + j];
      faceUV.u[j] = uv[index * 2];
      faceUV.v[j] = uv[index * 2 + 1];
      faceUV.nx[j] = normal[index * 3];
      faceUV.ny[j] = normal[index * 3 + 1];
      faceUV.nz[j] = normal[index * 3 + 2];
      faceUV.x[j] = position[index * 3];
      faceUV.y[j] = position[index * 3 + 1];
      faceUV.z[j] = position[index * 3 + 2];
    }
    const triSignedArea = signedArea2(faceUV.u[0], faceUV.v[0], faceUV.u[1], faceUV.v[1], faceUV.u[2], faceUV.v[2]);
    if (Math.abs(triSignedArea) < DIST_EPSILON_SQUARED) {
      // Ignore degenerate triangles in base geometry
      continue;
    }
    const flipWindings = triSignedArea < 0;
    const z = flipWindings ? 1 : -1;
    const uvClipPlanes: PlaneWithNormal[] = [];
    for (let v0 = 0; v0 < 3; v0++) {
      const v1 = (v0 + 1) % 3;
      const plane = planeByThreePoints(
        faceUV.u[v0],
        faceUV.v[v0],
        0,
        faceUV.u[v1],
        faceUV.v[v1],
        0,
        faceUV.u[v0],
        faceUV.v[v0],
        z,
      ) as Plane; // Guaranteed to be a valid plane
      const [nx, ny, nz] = planeNormal(plane);
      uvClipPlanes.push({ plane, nx, ny, nz });
    }
    ungroupedBaseGeometryFaces.push({ face: faceUV, uvClipPlanes, startIndex: i, flipWindings });
  }

  // Group faces based on the face normal.
  for (const face of ungroupedBaseGeometryFaces) {
    const f = face.face;
    const p = planeByThreePoints(f.x[0], f.y[0], f.z[0], f.x[1], f.y[1], f.z[1], f.x[2], f.y[2], f.z[2]);
    if (!p) {
      continue;
    }
    const [pnx, pny, pnz] = planeNormal(p);
    const pNorm = vec3Copy(vec3Normalize(pnx, pny, pnz));
    let found = false;
    // Search downwards, since it is likely the most recent plane will match the current one.
    for (let g = groupedFaces.length - 1; g >= 0 && !found; g--) {
      if (planeWithNormalIsCoplanar(groupPlanes[g], groupNormals[g], p, pNorm)) {
        groupedFaces[g].push(face);
        found = true;
      }
    }
    if (!found) {
      groupPlanes.push(p);
      groupNormals.push(pNorm);
      groupedFaces.push([face]);
    }
  }
  return { groupedFaces, groupNormals };
}

function adjustSdfPatternsForGeneration(
  sdfCommon: SdfCommonParamsPreprocessed,
  patterns: SdfPatternPreprocessed[],
): {
  sdfPatterns: SdfPatternPreprocessed[];
  sdfCellGeometries: GeoBuilder[];
} {
  const sdfCellGeometries: GeoBuilder[] = [];
  const adjustedSdfPatterns: SdfPatternPreprocessed[] = [];
  for (let patternIndex = 0; patternIndex < patterns.length; patternIndex++) {
    const pattern: SdfPatternPreprocessed = structuredClone(patterns[patternIndex]);

    // Detect materials that are continuous in one direction (like siding) and bump up distance and size so
    // that we generate less geometry.
    if (pattern.continuousX) {
      pattern.xDistance = 10000;
      pattern.xSize = 10001;
      pattern.cutXfromY = false;

      // We can avoid extra lines sometimes appearing on auto hatch materials by not having a SDF cell boundary right at the edge of the hatch viewport.
      // Before this workaround, these lines would appear on a small subset of materials.
      pattern.xOffset = -5000;
    }
    if (pattern.continuousY) {
      pattern.yDistance = 10000;
      pattern.ySize = 10001;

      // We can avoid extra lines sometimes appearing on auto hatch materials by not having a SDF cell boundary right at the edge of the hatch viewport.
      // Before this workaround, these lines would appear on a small subset of materials.
      pattern.yOffset = -5000;
    }

    const geo = fetchCellGeometry(pattern, sdfCommon, false, Infinity);

    adjustedSdfPatterns.push(pattern);
    sdfCellGeometries.push(geo);
  }
  return { sdfPatterns: adjustedSdfPatterns, sdfCellGeometries };
}

function generateSdfGeoFromCells(
  groupFaces: FaceAndPlanes[],
  pattern: SdfPatternPreprocessed,
  cellAABBs: Generator<number[]>,
  sdfCellGeometry: GeoBuilder,
  globalClipPlanes: PlaneWithNormal[],
  flipWindings: boolean,
  scale: Vec3,
  uvOffset: Vec2,
  geoBuilder: GeoBuilder,
) {
  const { vertices: cellVertices, indices: cellIndices } = sdfCellGeometry;

  let cellAABB: IteratorResult<number[], any> = cellAABBs.next();

  while (!cellAABB.done) {
    const [x, y, width, height, cellRand] = cellAABB.value;

    const vertices = new Float32Array(cellVertices.length);
    vertices.set(cellVertices);
    const indices = new Uint32Array(cellIndices.length);
    indices.set(cellIndices);

    if (Math.abs(width / pattern.xSize - 1) > 0.001 || Math.abs(height / pattern.ySize - 1) > 0.001) {
      scaleVerts(vertices, width / pattern.xSize, height / pattern.ySize, 1);
    }
    rotateVertsZ(vertices, pattern.rotation);

    // Apply position and vary height factor
    translateVerts(vertices, x, y, (cellRand - 0.5) * (pattern.varyHeight ? pattern.varyHeightScale : 0));

    const geo = convertUVBufferToWorldGeometry(
      vertices,
      indices,
      groupFaces,
      uvOffset,
      globalClipPlanes,
      flipWindings,
      scale,
    );

    geo.vertices = weldVertices(
      geo.vertices,
      geo.newIndices,
      WeldingDuplicateHandling.REMOVE_DUPLICATE_VERTICES,
      DIST_EPSILON,
    );

    geoBuilderAppend(geoBuilder, geo);

    cellAABB = cellAABBs.next();
  }
}

/**
 * This function converts a design material SDF into tessellated geometry.
 * There are up to four independent SDF patterns in each design material.
 * Each SDF pattern divides the UV space into a rotated grid, which is used as the basis of placing SDF cells.
 * The shape of the cell is generated by generateCellGeometry, and functions from sdfLayout are used to
 * determine the locations in UV space where the cells are placed.
 * After the geometry for the cells has been generated, it is clipped against the base geometry in UV space.
 * The resulting cell is transformed into world space and added to the `Mesh3` that gets returned.
 * Finally, UV's for the cells get offset to avoid overlap with the base geometry.
 *
 * @param baseGeo Base geometry
 * @param scale Scale to apply (needed for props)
 * @param adjustBaseGeo Whether to adjust base geometry based on design material thickness
 * @param params Design material parameters
 * @param currentUVLayout UV layout info (used to avoid uv overlap between different layers)
 * @param ignoreMaskOnlyParam Set this to true to generate geometry even when maskOnly is set.
 * This can be used for autogenerating hatch patterns for example.
 */
export function generateMesh3FromDesignMaterial(
  baseGeo: Mesh3,
  scale: Vec3,
  adjustBaseGeo: AdjustGeoType,
  sdf: SdfParamsPreprocessed,
  currentUVLayout: MaterialLayoutInfo,
  mergeCoplanarFaces: boolean = false,
  generateNormals: boolean = false,
  ignoreMaskOnlyParam: boolean = false,
  wrapSizeU?: number,
  wrapSizeV?: number,
): Mesh3 | undefined {
  if ((sdf.common.maskOnly && !ignoreMaskOnlyParam) || !baseGeo.uvs1) {
    return undefined;
  }

  const { groupedFaces, groupNormals } = gatherGroupedFacesAndPlanes(baseGeo);

  const { sdfPatterns, sdfCellGeometries } = adjustSdfPatternsForGeneration(sdf.common, sdf.patterns);

  const geoBuilder = geoBuilderNew();

  const processedVert = {};

  for (let groupIndex = 0, fCount = groupedFaces.length; groupIndex < fCount; groupIndex++) {
    const groupFaces = groupedFaces[groupIndex];
    const baseFaceUV = groupFaces[0].face;
    let adjustGeo = false;
    // This is used to adjust the base geometry to account for material thickness that we don't need to consider
    // when using SDF or parallax maps.  If we don't do this, wall material will stick out in front of exterior trim.
    switch (adjustBaseGeo) {
      case "ADJUST_ALL":
        adjustGeo = true;
        break;
      case "ADJUST_EXCLUDING_UPWARDS":
        {
          // This is basically a hack to not adjust base roof geo right now.
          const [fnx, fny, fnz] = groupNormals[groupIndex];
          adjustGeo = vec3Dot(fnx, fny, fnz, 0, 0, 1) < 0.1;
        }
        break;
      default:
        adjustGeo = false;
        break;
    }

    if (adjustGeo) {
      // Push the geometry inward so that the top of the SDF boxes is at the original surface of the geometry.
      let pushInward = 0.0;
      for (let i = 0; i < sdfPatterns.length; i++) {
        pushInward = Math.max(pushInward, sdfPatterns[i].zSize);
      }
      pushInward *= 1.025; // fudge!

      const { verts: position } = baseGeo;
      const posArray = position as Float32Array;
      for (const face of groupFaces) {
        for (let j = 0; j < 3; j++) {
          const index = baseGeo.tris[face.startIndex + j];
          face.face.x[j] += -face.face.nx[j] * pushInward * (1 / scale[0]);
          face.face.y[j] += -face.face.ny[j] * pushInward * (1 / scale[1]);
          face.face.z[j] += -face.face.nz[j] * pushInward * (1 / scale[2]);
          if (!processedVert[index]) {
            posArray[index * 3] = face.face.x[j];
            posArray[index * 3 + 1] = face.face.y[j];
            posArray[index * 3 + 2] = face.face.z[j];
            processedVert[index] = true;
          }
        }
      }
    }

    const flipWindings =
      signedArea2(
        baseFaceUV.u[0],
        baseFaceUV.v[0],
        baseFaceUV.u[1],
        baseFaceUV.v[1],
        baseFaceUV.u[2],
        baseFaceUV.v[2],
      ) < 0;

    const globalClipPlanes: PlaneWithNormal[] = [];
    const plane = planeNewXY();
    const [nx, ny, nz] = planeNormal(plane);
    globalClipPlanes.push({ plane, nx, ny, nz });

    const uvAABB = computeUvAABB(groupFaces);
    const uvOffset = getLayoutOffset(uvAABB, currentUVLayout);

    for (let patternIndex = 0; patternIndex < sdfPatterns.length; ++patternIndex) {
      const pattern = sdfPatterns[patternIndex];
      const cellBoundaries: Generator<number[]> = sdfCellBoundariesInAABB(
        sdf.common,
        sdf.randomTiles,
        sdfPatterns[patternIndex],
        patternIndex,
        uvAABB,
        wrapSizeU !== undefined,
        wrapSizeU,
        wrapSizeV,
      );

      generateSdfGeoFromCells(
        groupFaces,
        pattern,
        cellBoundaries,
        sdfCellGeometries[patternIndex],
        globalClipPlanes,
        flipWindings,
        scale,
        uvOffset,
        geoBuilder,
      );
    }
  }

  clearCellGeometryCache();

  // Adjust base geo UV's so they don't overlap.
  const uvArray = geoBuilder.uvs;
  if (uvArray.length > 0) {
    const aabb = aabb2ByArray([uvArray]) as AABB2;
    for (let i = 0, l = uvArray.length; i < l; i += 2) {
      aabb2AddPoint(aabb, uvArray[i], uvArray[i + 1]);
    }
    const [offsetU, offsetV] = getLayoutOffset(aabb, currentUVLayout);
    for (let i = 0, l = uvArray.length; i < l; i += 2) {
      uvArray[i] += offsetU;
      uvArray[i + 1] += offsetV;
    }
  }

  return compactMesh3FromGeoBuilder(geoBuilder, { mergeCoplanarFaces, generateMissingNormals: generateNormals });
}
